/**
 * 邀请服务
 * 处理邀请有礼相关的 API 调用
 * 使用统一的 userApiClient 进行 API 调用
 */

import { userApiClient } from "@/lib/userApiClient";

// ============ 类型定义 ============

/** 邀请记录 */
export interface Invitation {
  id: number;
  token: string;
  status: "pending" | "accepted" | "expired";
  created_at: string;
  accepted_at?: string;
  expires_at?: string;
  invitee_name?: string;
  invitee_email?: string;
}

/** 邀请统计 */
export interface InvitationStats {
  total_sent: number;
  total_accepted: number;
  total_pending: number;
  monthly_accepted: number;
  monthly_points_limit: number;
  monthly_points_earned: number;
  invited_users_count: number;
}

/** 邀请列表响应 */
export interface InvitationListResponse {
  invitations: Invitation[];
  stats: InvitationStats;
}

/** 创建邀请响应 */
export interface CreateInvitationResponse {
  token: string;
  invite_url: string;
  expires_at: string;
  created_at: string;
}

/** 邀请人信息 */
export interface InviterInfo {
  name: string;
  avatar_url?: string;
}

/** 验证邀请响应 */
export interface VerifyInvitationResponse {
  valid: boolean;
  inviter?: InviterInfo;
  reward_points?: number;
  error?: string;
}

// ============ 服务类 ============

/**
 * 邀请服务类
 * 提供邀请有礼相关的 API 操作
 */
class InvitationService {
  /**
   * 获取邀请列表和统计
   * @returns 邀请列表和统计数据
   */
  async getInvitations(): Promise<InvitationListResponse> {
    return userApiClient.get<InvitationListResponse>("/invitations");
  }

  /**
   * 获取邀请统计
   * @returns 统计数据
   */
  async getStats(): Promise<InvitationStats> {
    return userApiClient.get<InvitationStats>("/invitations/stats");
  }

  /**
   * 创建新邀请
   * @returns 新邀请信息
   */
  async createInvitation(): Promise<CreateInvitationResponse> {
    return userApiClient.post<CreateInvitationResponse>("/invitations");
  }

  /**
   * 验证邀请码
   * @param token 邀请码
   * @returns 验证结果
   */
  async verifyInvitation(token: string): Promise<VerifyInvitationResponse> {
    return userApiClient.get<VerifyInvitationResponse>("/invitations/verify", {
      params: { token },
    });
  }
}

// 导出单例
export const invitationService = new InvitationService();
export default invitationService;
